import { useMemo, useCallback } from 'react'
import { reorderPhotoSessionMedia, reorderGroupMedia, getApiErrorMessage } from '../api'
import { useToast } from '../contexts/ToastContext'
import { useI18n } from '../i18n'
import { groupMediaBySession } from '../pages/ModelDetail/utils'
import useMediaSync from './useMediaSync'
import useMediaSelection from './useMediaSelection'
import useSessions from './useSessions'

/**
 * Композитный хук: объединяет useMediaSync + useMediaSelection + useSessions.
 * Публичный API намеренно сохранён прежним для обратной совместимости с ModelDetailMediaSection.
 */
export default function useModelMedia(modelId, model, onModelUpdate, mediaParams = {}) {
  const { t } = useI18n()
  const { addToast } = useToast()
  const {
    media,
    setMedia,
    mediaTotal,
    mediaHasMore,
    loadingMedia,
    describingPhotos,
    mediaError,
    downloadQueueCount,
    mediaUpdatesRef,
    loadMedia,
    handleSyncMedia,
    handleCancelSyncMedia,
    handleDownloadAllFullMedia,
    handleDescribePhotos,
    handleDownloadOneFullMedia,
    updateMediaItem,
  } = useMediaSync(modelId, model, onModelUpdate, mediaParams)

  const { selectedIds, hasSelection, toggleMediaSelection, clearSelection } = useMediaSelection()

  const sessions = useSessions(
    modelId,
    model,
    onModelUpdate,
    selectedIds,
    setMedia,
    clearSelection,
    loadMedia,
  )

  const mediaGroups = useMemo(
    () => groupMediaBySession(media || [], sessions.sessions),
    [media, sessions.sessions],
  )

  const handleReorderMedia = useCallback(
    async (sessionId, mediaIds, groupId = null, onApplied) => {
      if (!modelId || !mediaIds?.length) return
      const orderKey = groupId != null ? 'group_sort_order' : 'session_sort_order'
      const filterFn = groupId != null
        ? (m) => m.photo_session_id === sessionId && m.media_group_id === groupId
        : (m) => m.photo_session_id === sessionId && m.media_group_id == null
      setMedia((prev) => {
        if (!prev) return prev
        const groupMedia = prev.filter(filterFn)
        if (groupMedia.length !== mediaIds.length) return prev
        const idToItem = new Map(groupMedia.map((m) => [m.id, m]))
        const reordered = mediaIds
          .map((id, index) => {
            const item = idToItem.get(id)
            return item ? { ...item, [orderKey]: index } : null
          })
          .filter(Boolean)
        if (reordered.length !== mediaIds.length) return prev
        const reorderedIds = new Set(reordered.map((m) => m.id))
        return prev.map((m) => {
          const updated = reordered.find((r) => r.id === m.id)
          return updated ?? m
        })
      })
      onApplied?.()
      try {
        if (groupId != null) {
          await reorderGroupMedia(modelId, sessionId, groupId, mediaIds)
        } else {
          await reorderPhotoSessionMedia(modelId, sessionId, mediaIds)
        }
        addToast(t('toast.order.changed'), 'success')
      } catch (e) {
        const msg = getApiErrorMessage(e, t('toast.error.reorderFailed'))
        loadMedia()
        onModelUpdate?.({ error: msg })
      }
    },
    [modelId, loadMedia, onModelUpdate, t]
  )

  return {
    // media
    media,
    setMedia,
    mediaTotal,
    mediaHasMore,
    loadingMedia,
    describingPhotos,
    mediaError,
    downloadQueueCount,
    mediaUpdatesRef,
    mediaGroups,
    loadMedia,
    handleSyncMedia,
    handleCancelSyncMedia,
    handleDownloadAllFullMedia,
    handleDescribePhotos,
    handleDownloadOneFullMedia,
    updateMediaItem,
    handleReorderMedia,
    // selection
    selectedIds,
    hasSelection,
    toggleMediaSelection,
    clearSelection,
    // sessions
    ...sessions,
  }
}
